import { StarFilled, StarOutlined } from '@ant-design/icons';
import { Button, Layout, PageHeader, Space, Spin, Table } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CellValue } from '../../elements';
import { Field, Grid, GridCell, GridRow } from '../../models';
import { apiClient, apiErrorMessage } from '../../utils';
import { CellEditPage, RowCardPage } from '../grids';
import { FloatingModal } from '../../elements';

export function SearchPage(props: { phrase?: string; onSearchSaved?: () => void }) {
  const { Content } = Layout;

  const [highlight, setHighlight] = useState<string[]>();

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cellResults, setCellResults] = useState<any[]>();
  const [rowResultGrid, setRowResultGrid] = useState<Grid>();
  const [rowResult, setRowResult] = useState<GridRow>();
  const [rowCardModalOpen, setRowCardModalOpen] = useState<any>();
  const [cellEditModalOpen, setCellEditModalOpen] = useState<any>();
  const [grids, setGrids] = useState<Grid[]>();

  const [tableCurrent, setTableCurrent] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotal, setTableTotal] = useState(0);

  useEffect(() => {
    apiClient.get(`grids`).then((response: any) => {
      const responseGrids = response.data as Grid[];
      setGrids(responseGrids);
    });
  }, []);

  useEffect(() => {
    if (!props.phrase) {
      setCellResults(undefined);
      return;
    }

    const loadResults = () => {
      if (!grids) {
        return;
      }

      setLoading(true);
      setSaved(false);

      let offset = (tableCurrent - 1) * tablePageSize;
      offset = offset < 0 ? 0 : offset;
      const queryParams = [`keyword=${props.phrase}`, `limit=${tablePageSize}`, `offset=${offset}`];
      const queryParamsStr = queryParams.join('&');

      apiClient
        .get(`search?${queryParamsStr}`)
        .then(response => {
          if (Array.isArray(response.data.cells)) {
            setHighlight(
              props.phrase
                ?.replace(' IN ', ' in ')
                .split(' in ')[0]
                .replaceAll(' and ', ' ')
                .replaceAll(' or ', ' ')
                .replaceAll('(', ' ')
                .replaceAll(')', ' ')
                .split(' ')
                .filter(h => !!h)
                .map(h => h.replace(/[*]/g, '')),
            );
            setRowResult(undefined);
            setCellResults(response.data.cells);
            setTableTotal(response.data.count);
          } else {
            setCellResults(undefined);
            setRowResult(response.data as GridRow);
            setRowResultGrid(grids?.find(g => g.id === response.data.gridId));
            setTableTotal(1);
          }

          setLoading(false);
        })
        .catch((reason: any) => {
          apiErrorMessage(reason);
          setLoading(false);
        });
    };

    loadResults();
  }, [props.phrase, tableCurrent, tablePageSize, grids]);

  const saveSearch = () => {
    if (saved) {
      return;
    }
    setSaveLoading(true);
    apiClient
      .post('users/me/settings/searches', { phrase: props.phrase })
      .then(() => {
        setSaved(true);
        if (props.onSearchSaved) {
          props.onSearchSaved();
        }
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSaveLoading(false);
      });
  };

  const openRowCardModal = (rowId: string, gridId: string) => {
    setRowCardModalOpen({ rowId, gridId });
  };

  const closeRowCardModal = () => {
    setRowCardModalOpen(undefined);
  };

  const openCellEditModal = (field: Field, rowId: string) => {
    const grid = grids?.find(g => g.id === field.gridId);
    if (!grid) {
      return;
    }
    setCellEditModalOpen({ row: null, grid, gridId: field.gridId, field, rowId });
  };

  const closeCellEditModal = () => {
    setCellEditModalOpen(undefined);
  };

  const onTableChange = (pagination: any, filters: any, sorter: any) => {
    setTableCurrent(pagination.current || 1);
    setTablePageSize(pagination.pageSize);
  };

  const tableColumns = [
    {
      title: 'Veri seti',
      dataIndex: ['field', 'grid', 'name'],
      key: 'grid',
    },
    {
      title: 'Veri elemanı',
      dataIndex: ['field', 'name'],
      key: 'field',
    },
    {
      title: 'Değer',
      dataIndex: 'cell',
      key: 'cell',
      render: (cell: GridCell, result: any) => (
        <div style={{ minWidth: '1rem', display: 'inline-block' }}>
          <CellValue cell={cell} originalValue={cell?.value} field={result.field} highlight={highlight} />
        </div>
      ),
    },
    {
      title: 'Son düzenleme',
      dataIndex: ['cell', 'updatedAt'],
      key: 'updatedAt',
      render: (updatedAt: Date) => moment(updatedAt).format('DD.MM.yyyy HH:mm:ss'),
    },
    {
      title: null,
      dataIndex: 'actions',
      key: 'actions',
      render: (_: any, result: any) => (
        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={() => openCellEditModal(result.field, result.recordId)}>Detaylar</Button>
            <Button onClick={() => openRowCardModal(result.recordId, result.field.gridId)}>Kart</Button>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={'"' + props.phrase + '" için arama sonuçları'}
        style={{ paddingLeft: 0, paddingRight: 0 }}
        extra={
          <Button type="link" icon={saved ? <StarFilled /> : <StarOutlined />} loading={saveLoading} onClick={saveSearch}>
            {saved ? 'Arama kaydedildi' : 'Aramayı kaydet'}
          </Button>
        }
      />
      <Content style={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
        <Spin spinning={loading}>
          {cellResults && (
            <Table
              columns={tableColumns}
              dataSource={cellResults}
              rowKey={row => row?.cell?.id}
              loading={loading}
              pagination={{
                current: tableCurrent,
                total: tableTotal,
                pageSize: tablePageSize,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomCenter'],
                showTotal: (total, range) => {
                  let start = range[0];
                  if (start < 1) {
                    start = 1;
                  }
                  let end = range[1];
                  if (end === 0) {
                    end = total;
                  }
                  return `Toplam ${total} kayıt. ${start}-${end} arası gösteriliyor.`;
                },
                showSizeChanger: true,
              }}
              onChange={onTableChange}
            />
          )}
          {rowResult && (
            <div style={{ borderTop: '1px solid #ccc', paddingTop: '1rem' }}>
              <RowCardPage grid={rowResultGrid} rowId={props.phrase} onSaved={() => {}} />
            </div>
          )}
        </Spin>
      </Content>
      <FloatingModal
        title="Kayıt detayları"
        visible={rowCardModalOpen}
        footer={null}
        onCancel={closeRowCardModal}
        destroyOnClose={true}
        width={'50%'}
      >
        <RowCardPage gridId={rowCardModalOpen?.gridId} rowId={rowCardModalOpen?.rowId} onSaved={() => {}} />
      </FloatingModal>
      <FloatingModal
        title="Veri detayları"
        visible={cellEditModalOpen}
        footer={null}
        onCancel={closeCellEditModal}
        destroyOnClose={true}
        width={'90%'}
      >
        <CellEditPage
          record={cellEditModalOpen?.row}
          grid={cellEditModalOpen?.grid}
          gridId={cellEditModalOpen?.gridId}
          rowId={cellEditModalOpen?.rowId}
          field={cellEditModalOpen?.field}
          allowRecordWithErrors={cellEditModalOpen?.grid?.allowRecordWithErrors}
          onSaved={() => {}}
        />
      </FloatingModal>
    </>
  );
}
